import { CreditCard, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PaywallScreenProps {
  /** Mensagem vinda do backend (motivo do bloqueio). */
  message?: string | null;
  /** Código de bloqueio (`BILLING_TRIAL_EXPIRED`, `BILLING_PAST_DUE`, ...). */
  code?: string | null;
  /**
   * Se o usuário atual é o responsável financeiro (billing owner). Só o owner
   * vê o CTA de reativação — os demais veem uma orientação para contatá-lo
   * (não conseguem pagar). Default `false` (mostra a mensagem de contato).
   */
  isBillingOwner?: boolean;
}

/**
 * Mapeia o código de bloqueio para um título amigável.
 */
function titleForCode(code?: string | null): string {
  switch (code) {
    case 'BILLING_TRIAL_EXPIRED':
      return 'Seu período de teste terminou';
    case 'BILLING_PAST_DUE':
      return 'Há uma fatura em aberto';
    case 'BILLING_SUSPENDED':
      return 'Sua assinatura está inativa';
    default:
      return 'Adicione uma forma de pagamento';
  }
}

/**
 * PaywallScreen — bloqueio de acesso por billing do TENANT (HTTP 402).
 *
 * Exibida quando o `BillingStatusGuard` retorna 402 (`BILLING_*`): trial
 * expirado, inadimplência ou assinatura inativa.
 *
 * Apenas o **billing owner** vê o CTA "Reativar assinatura" (leva à tela de
 * **Faturamento** `/billing`, allowlistada no `BillingGate`). Os demais usuários
 * NÃO conseguem pagar, então veem uma orientação para contatar o responsável
 * financeiro — sem CTA de pagamento. (Na prática o owner é redirecionado pelo
 * `BillingGate` antes de ver esta tela; a gating por owner aqui é defensiva.)
 *
 * Epic #884 §3.1 / Fase 3.
 */
export function PaywallScreen({
  message,
  code,
  isBillingOwner = false,
}: PaywallScreenProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md rounded-xl border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Lock className="h-7 w-7 text-primary" aria-hidden="true" />
        </div>

        <h1 className="text-xl font-semibold text-foreground">
          {titleForCode(code)}
        </h1>

        <p className="mt-3 text-sm text-muted-foreground">
          {message ??
            'Para continuar usando a plataforma, regularize a assinatura.'}
        </p>

        {isBillingOwner ? (
          <Button asChild className="mt-6 w-full" size="lg">
            <a href="/billing">
              <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
              Reativar assinatura
            </a>
          </Button>
        ) : (
          <p className="mt-6 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
            Entre em contato com o responsável financeiro da sua empresa para
            reativar a assinatura.
          </p>
        )}
      </div>
    </div>
  );
}
